import type { AppShellNavbarConfiguration } from '@mantine/core';
import { useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { createStore, useSelector } from '@tanstack/react-store';
import { useCallback, useMemo } from 'react';

interface LeftSidebarState extends AppShellNavbarConfiguration {
	collapsed: {
		desktop: boolean;
		mobile: boolean;
	};
}

export const leftSidebarStore = createStore<LeftSidebarState>({
	width: 360,
	breakpoint: 'sm',
	collapsed: {
		desktop: false,
		mobile: true,
	},
});

export function initializeLeftSidebar(config: Partial<LeftSidebarState>) {
	leftSidebarStore.setState(prev => ({
		...prev,
		...config,
	}));
}

export function toggleLeftSidebar() {
	leftSidebarStore.setState(prev => ({
		...prev,
		collapsed: {
			...prev.collapsed,
			desktop: !prev.collapsed.desktop,
			mobile: !prev.collapsed.mobile,
		},
	}));
}

export function useLeftSidebarProps() {
	return useSelector(leftSidebarStore);
}

export function useLeftSidebarCollapsed() {
	const theme = useMantineTheme();
	const collapsedState = useSelector(leftSidebarStore, x => x.collapsed);
	const isSmallerThanBreakpoint = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`, false, {
		getInitialValueInEffect: false,
	});

	const close = useCallback(() => {
		leftSidebarStore.setState(prev => ({
			...prev,
			collapsed: {
				...prev.collapsed,
				desktop: isSmallerThanBreakpoint ? prev.collapsed.desktop : true,
				mobile: isSmallerThanBreakpoint ? true : prev.collapsed.mobile,
			},
		}));
	}, [isSmallerThanBreakpoint]);

	const isCollapsed = useMemo(
		() => (isSmallerThanBreakpoint ? collapsedState.mobile : collapsedState.desktop) ?? false,
		[collapsedState, isSmallerThanBreakpoint],
	);

	return useMemo(
		() => ({
			isCollapsed,
			toggle: toggleLeftSidebar,
			close,
			isSmallerThanBreakpoint,
		}),
		[isCollapsed, close, isSmallerThanBreakpoint],
	);
}
